import { css } from "styled-system/css";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { BookingWizard, type InitialRescheduleState } from "@/components/book/booking-wizard";
import { BookPublicLayout } from "@/components/book/book-public-layout";
import { BookTabs, type BookTabId } from "@/components/book/book-tabs";
import { BookInfoTab } from "@/components/book/book-info-tab";
import { PrivateAgendaContact } from "@/components/book/private-agenda-contact";
import { PatientProgramGate } from "@/components/book/patient-program-gate";
import { LoadingFrame } from "@/components/ui/loading-frame";
import type { AmenityItem } from "@/lib/amenities-catalog";

type ProfJson = {
  name: string;
  slug: string;
  bio: string | null;
  information?: string | null;
  category: string | null;
  specialty?: string | null;
  modality?: string;
  profileTheme?: string;
  avatarUrl: string | null;
  primaryColor: string;
  timezone: string;
  flexibleSchedule?: boolean;
  address?: string | null;
  addressLat?: number | null;
  addressLng?: number | null;
  amenities?: AmenityItem[];
  cancellationPolicy?: { hoursBeforeCancel: number; penaltyType: string };
  staff?: { id: string; name: string; googleColorId: string }[];
  publicBookingEnabled?: boolean;
  waitlistEnabled?: boolean;
  phone?: string | null;
  services: {
    id: string;
    kind?: "service" | "court";
    name: string;
    description: string | null;
    duration: number;
    price: number | null;
    requiresDeposit: boolean;
    depositAmount: number | null;
    depositType: string | null;
    depositCurrency: string | null;
    /** Solo presente si el servicio viene de un programa de tratamiento activo del paciente. */
    sessionsRemaining?: number;
  }[];
};

type ProfServices = ProfJson["services"];
type ProfService = ProfServices[number];

type WaitlistValidateResponse = {
  valid: boolean;
  serviceId?: string | null;
  serviceName?: string | null;
  duration?: number | null;
  price?: number | null;
  requiresDeposit?: boolean;
  depositAmount?: number | null;
  depositType?: string | null;
  depositCurrency?: string | null;
  startTimeIso?: string;
};

type WlValidation =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "valid"; service: ProfService; startTimeIso: string }
  | { status: "invalid" };

function bookWelcomeSubtitle(info: string | null | undefined, bio: string | null | undefined): string {
  const fromHtml = (info ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (fromHtml.length >= 8) return fromHtml.slice(0, 220);
  const fromBio = (bio ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (fromBio.length >= 8) return fromBio.slice(0, 220);
  return "¡Bienvenidos! Seleccioná el servicio para reservar.";
}

export function BookPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [prof, setProf] = useState<ProfJson | null | "missing">(null);
  const [activeTab, setActiveTab] = useState<BookTabId>("reservar");

  // Programas de tratamiento: hasta que el paciente se identifique (o decida saltear el paso),
  // no sabemos si mostrar sus servicios de programa. `gateServices` queda en null mientras tanto
  // (oculta el wizard); una vez resuelto, contiene la lista final de servicios a reservar.
  const [gateServices, setGateServices] = useState<ProfServices | null>(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState("");

  const rescheduleState = (location.state as { reschedule?: InitialRescheduleState } | null)?.reschedule ?? null;

  // Lista de espera: ?wl=TOKEN llega desde el WhatsApp de "se liberó un turno". Mientras se
  // valida no sabemos si mostrar el gate de programas o directo el turno pre-cargado.
  const wlToken = searchParams.get("wl");
  const [wlValidation, setWlValidation] = useState<WlValidation>(wlToken ? { status: "checking" } : { status: "idle" });

  useEffect(() => {
    if (!wlToken) return;
    let cancelled = false;
    void fetch(`/api/public/waitlist/validate?token=${encodeURIComponent(wlToken)}`)
      .then((r) => (r.ok ? (r.json() as Promise<WaitlistValidateResponse>) : ({ valid: false } as WaitlistValidateResponse)))
      .then((j) => {
        if (cancelled) return;
        if (!j.valid || !j.serviceId || !j.startTimeIso) {
          setWlValidation({ status: "invalid" });
          return;
        }
        setWlValidation({
          status: "valid",
          startTimeIso: j.startTimeIso,
          service: {
            id: j.serviceId,
            kind: "service",
            name: j.serviceName ?? "Consulta",
            description: null,
            duration: j.duration ?? 30,
            price: j.price ?? null,
            requiresDeposit: j.requiresDeposit ?? false,
            depositAmount: j.depositAmount ?? null,
            depositType: j.depositType ?? "fixed",
            depositCurrency: j.depositCurrency ?? "ARS",
          },
        });
      })
      .catch(() => {
        if (!cancelled) setWlValidation({ status: "invalid" });
      });
    return () => {
      cancelled = true;
    };
  }, [wlToken]);

  const waitlistLock = useMemo(() => {
    if (wlValidation.status !== "valid") return null;
    return { serviceId: wlValidation.service.id, startTimeIso: wlValidation.startTimeIso };
  }, [wlValidation]);

  const payment = searchParams.get("payment") ?? undefined;
  const cancelled = searchParams.get("cancelled") ?? undefined;
  const msg = searchParams.get("msg") ?? undefined;
  const banner = { payment, cancelled, msg };

  // El turno ofrecido por lista de espera puede ser un servicio "programOnly" oculto de la lista
  // general — lo unimos a mano para que BookingWizard pueda resolverlo aunque no esté ahí.
  const effectiveServicesForWizard = useMemo(() => {
    const base = gateServices ?? (prof && prof !== "missing" ? prof.services : []);
    if (wlValidation.status === "valid" && !base.some((s) => s.id === wlValidation.service.id)) {
      return [wlValidation.service, ...base];
    }
    return base;
  }, [gateServices, prof, wlValidation]);

  useEffect(() => {
    if (!slug) return;
    void fetch(`/api/public/professional/${encodeURIComponent(slug)}`).then((r) => {
      if (r.status === 404) {
        setProf("missing");
        return;
      }
      void r.json().then((j: ProfJson) => {
        setProf(j);
        // Reprogramación: saltear el paso de email y buscar directo con el email del turno
        // original, para que el servicio (posiblemente de programa) siga disponible.
        const rescheduleEmail = rescheduleState?.clientEmail?.trim();
        if (rescheduleEmail) {
          setPrefillEmail(rescheduleEmail);
          void fetch(`/api/public/professional/${encodeURIComponent(slug)}?email=${encodeURIComponent(rescheduleEmail)}`)
            .then((r2) => (r2.ok ? r2.json() : null))
            .then((j2: ProfJson | null) => setGateServices(j2?.services ?? j.services))
            .catch(() => setGateServices(j.services));
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function handleGateContinue(email: string) {
    if (!slug) return;
    setGateBusy(true);
    try {
      const r = await fetch(`/api/public/professional/${encodeURIComponent(slug)}?email=${encodeURIComponent(email)}`);
      const services = r.ok ? ((await r.json()) as ProfJson).services : (prof && prof !== "missing" ? prof.services : []);
      setPrefillEmail(email);
      setGateServices(services);
    } finally {
      setGateBusy(false);
    }
  }

  function handleGateSkip() {
    if (prof && prof !== "missing") setGateServices(prof.services);
  }

  if (prof === null) {
    return (
      <div
        className={css({
          minH: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "6",
          bg: "#f4f6f8",
        })}
      >
        <div className={css({ w: "100%", maxW: "720px" })}>
          <LoadingFrame variant="light" minHeight={360} />
        </div>
      </div>
    );
  }

  if (prof === "missing" || !slug) {
    return (
      <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6" })}>
        <p className={css({ color: "#64748b" })}>Profesional no encontrado.</p>
      </div>
    );
  }

  const hasInfo = Boolean(prof.address?.trim()) || (prof.amenities?.length ?? 0) > 0;
  const bookingEnabled = prof.publicBookingEnabled ?? true;

  return (
    <BookPublicLayout
      slug={slug}
      name={prof.name}
      category={prof.category}
      specialty={prof.specialty}
      avatarUrl={prof.avatarUrl}
      primaryColor={prof.primaryColor ?? "#4f46e5"}
      profileTheme={prof.profileTheme ?? "classic"}
      welcomeSubtitle={bookWelcomeSubtitle(prof.information, prof.bio)}
    >
      {!bookingEnabled ? (
        <PrivateAgendaContact name={prof.name} phone={prof.phone ?? null} />
      ) : (
        <>
          {hasInfo ? (
            <BookTabs active={activeTab} onChange={setActiveTab} primaryColor={prof.primaryColor ?? "#4f46e5"} />
          ) : null}

          {activeTab === "info" && hasInfo ? (
            <BookInfoTab
              address={prof.address ?? null}
              lat={prof.addressLat ?? null}
              lng={prof.addressLng ?? null}
              amenities={prof.amenities ?? []}
              primaryColor={prof.primaryColor ?? "#4f46e5"}
            />
          ) : wlValidation.status === "checking" ? (
            <LoadingFrame variant="light" minHeight={200} />
          ) : (
            <>
              {wlValidation.status === "valid" ? (
                <div
                  className={css({
                    mb: "4",
                    px: "4",
                    py: "3",
                    rounded: "lg",
                    bg: "#dcfce7",
                    borderWidth: "1px",
                    borderColor: "#86efac",
                    fontSize: "sm",
                    color: "#15803d",
                    fontWeight: "medium",
                  })}
                >
                  ¡Llegaste a tiempo! Completá tus datos para confirmar el turno.
                </div>
              ) : null}
              {wlValidation.status === "invalid" ? (
                <div
                  className={css({
                    mb: "4",
                    px: "4",
                    py: "3",
                    rounded: "lg",
                    bg: "#fef9c3",
                    borderWidth: "1px",
                    borderColor: "#fde68a",
                    fontSize: "sm",
                    color: "#854d0e",
                  })}
                >
                  Este turno ya no está disponible. Podés anotarte nuevamente en lista de espera.
                </div>
              ) : null}

              {wlValidation.status !== "valid" && gateServices === null ? (
                <PatientProgramGate
                  primaryColor={prof.primaryColor ?? "#4f46e5"}
                  busy={gateBusy}
                  onContinue={(email) => void handleGateContinue(email)}
                  onSkip={handleGateSkip}
                />
              ) : (
                <BookingWizard
                  slug={slug}
                  primaryColor={prof.primaryColor ?? "#4f46e5"}
                  category={prof.category}
                  modality={prof.modality ?? "presencial"}
                  services={effectiveServicesForWizard}
                  staff={prof.staff ?? []}
                  timezone={prof.timezone}
                  flexibleSchedule={prof.flexibleSchedule ?? false}
                  cancellationPolicy={prof.cancellationPolicy}
                  banner={banner}
                  prefillEmail={prefillEmail}
                  waitlistEnabled={prof.waitlistEnabled ?? false}
                  waitlistToken={wlValidation.status === "valid" ? wlToken : null}
                  waitlistLock={waitlistLock}
                />
              )}
            </>
          )}
        </>
      )}
    </BookPublicLayout>
  );
}
